'use client';

import {useAtom} from 'jotai';
import Image from 'next/image';
import {useEffect, useState} from 'react';
import {useRouter} from 'next/navigation';
import {atomWithStorage} from 'jotai/utils';
import {useSessionStorage} from 'usehooks-ts';
import {AnimatePresence, domMax, LazyMotion, m as motion} from 'motion/react';
import {IconAspectRatio, IconAspectRatioFilled, IconX} from '@tabler/icons-react';
import addQuizSubmission from 'server/serverActions/addQuizSubmission';
import getQuizColorsFromBlur from 'src/utils/getQuizColorsFromBlur';
import {useServerAction} from 'src/hooks/useServerFormAction';
import QuizFinalPageContent from './QuizFinalPageContent';
import QuizQuestionContent from './QuizQuestionContent';
import toUrlSafeString from 'src/utils/toUrlSafeString';
import getImageUrl from 'src/utils/getImageUrl';
import recordEvent from 'src/utils/recordEvent';
import useMounted from 'src/hooks/useMounted';
import useSession from 'src/hooks/useSession';
import BaseButton from '../BaseButton';

const respectAspectRatioAtom = atomWithStorage<boolean>('quiz-aspect-ratio', true);

type Props = {
  quiz: QuizWithAuthorAndPlaces;
  seed: number;
  initialAnswer?: string;
};

const QuizGamePageContent = ({quiz, seed, initialAnswer}: Props) => {
  const router = useRouter();
  const {session} = useSession();

  const isMounted = useMounted();
  const [respectAspectRatio, setRespectAspectRatio] = useAtom(respectAspectRatioAtom);

  const [givenAnswers, setGivenAnswers, resetAnswers] = useSessionStorage<Record<string, string>>(
    `given_answers_${quiz._id}`,
    {}
  );
  const numberOfAnswers = Object.keys(givenAnswers).length;

  const [currentIndex, setCurrentIndex] = useState(numberOfAnswers);

  const isDone = currentIndex === quiz.questions.length;

  const [isFinalPageShown, setIsFinalPageShown] = useState(isDone);

  const playAgain = () => {
    setGivenAnswers({});
    setCurrentIndex(0);
    setIsFinalPageShown(false);
  };

  // const skipToEnd = () => {
  //   const answered = quiz.questions.map((question) => {
  //     const randomAnswerIndex = getRandomInteger(0, 3);

  //     return [question.id, question.answers[randomAnswerIndex]];
  //   });

  //   const answeredObject = answered.reduce(
  //     (acc, curr) => {
  //       acc[curr[0]] = curr[1];

  //       return acc;
  //     },
  //     {} as Record<string, string>
  //   );

  //   setGivenAnswers(answeredObject);
  //   setCurrentIndex(quiz.questions.length);

  //   setTimeout(() => setIsFinalPageShown(true), 500);
  // };

  const {action: submitQuiz} = useServerAction(addQuizSubmission);

  const setAnswer = (answer: string, questionId: string) => {
    const newGivenAnswers = {...givenAnswers, [questionId]: answer};

    setGivenAnswers(newGivenAnswers);

    const isLastQuestion = quiz.questions[quiz.questions.length - 1].id === questionId;

    if (isLastQuestion) {
      submitQuiz({quiz: quiz._id, answers: newGivenAnswers});
      if (!session?.isAdmin) recordEvent('submitQuiz', {props: {quiz: quiz.nameId}});
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (initialAnswer && quiz.questions[0]) {
        const answer = quiz.questions[0].answers?.find((answer) => toUrlSafeString(answer) === initialAnswer);

        if (answer) setAnswer(answer, quiz.questions[0].id);
      }
    }, 800);

    return () => clearTimeout(timeout);
  }, [initialAnswer]);

  const goToNextQuestion = () => {
    const isLastQuestion = currentIndex === quiz.questions.length - 1;

    setCurrentIndex((prev) => prev + 1);

    if (isLastQuestion) setTimeout(() => setIsFinalPageShown(true), 500);
  };

  const {cssVariables} = getQuizColorsFromBlur(
    quiz.questions[isDone ? quiz.questions.length - 1 : currentIndex]?.image?.blurHash
  );

  if (!isMounted) return null;

  return (
    <LazyMotion features={domMax} strict>
      {quiz.questions[currentIndex + 1] ? (
        <div className="absolute w-2 h-2 opacity-0 z-0">
          <Image
            src={getImageUrl(quiz.questions[currentIndex + 1]?.image?.path)}
            alt=""
            aria-hidden="true"
            priority
            fill
          />
        </div>
      ) : null}

      <div
        style={cssVariables}
        className="transition-colors duration-1000 [&_.h-button]:focus-visible:ring-(--quiz-text-color) dark:[--quiz-bg-color:var(--dark-quiz-bg-color)]! dark:[--quiz-text-color:var(--dark-quiz-text-color)]! bg-(--quiz-bg-color)"
      >
        <AnimatePresence initial={false} mode="wait">
          {isFinalPageShown ? (
            <QuizFinalPageContent key="final-page" quiz={quiz} givenAnswers={givenAnswers} playAgain={playAgain} />
          ) : (
            <motion.div
              key="quiz-page"
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              transition={{duration: 0.6}}
              className="relative flex items-center flex-col min-h-dvh w-full overflow-hidden [--side-padding:48px] max-xl:[--side-padding:24px] max-md:[--side-padding:12px]"
            >
              <div className="flex items-center justify-between py-4 max-xl:py-2 w-full gap-4 max-md:gap-2 px-(--side-padding)">
                <div className="text-(--quiz-text-color) shrink-0 opacity-60 w-[200px] max-[1200px]:w-auto">
                  {currentIndex + 1} / {quiz.questions.length}
                </div>

                <h1 className="text-lg max-sm:text-base font-medium font-heading text-(--quiz-text-color) opacity-60 truncate">
                  {quiz.name}
                </h1>

                <div className="flex justify-end items-center gap-4 max-md:gap-0 w-[200px] max-[1200px]:w-auto">
                  {/* <BaseButton
                    TablerIcon={IconPennantFilled}
                    buttonVariant="text"
                    iconSize={22}
                    onPress={skipToEnd}
                    className="w-[42px] opacity-60"
                    color="var(--quiz-text-color)"
                  /> */}

                  <BaseButton
                    TablerIcon={respectAspectRatio ? IconAspectRatioFilled : IconAspectRatio}
                    iconSize={22}
                    buttonVariant="text"
                    className="w-[42px] opacity-60"
                    color="var(--quiz-text-color)"
                    onPress={() => setRespectAspectRatio((prev) => !prev)}
                  />

                  <BaseButton
                    TablerIcon={IconX}
                    iconSize={24}
                    buttonVariant="text"
                    color="var(--quiz-text-color)"
                    className="w-[42px] opacity-60"
                    onPress={() => {
                      router.back();
                      resetAnswers();
                    }}
                  />
                </div>
              </div>

              <QuizQuestionContent
                cssVariables={cssVariables}
                quizQuestion={quiz.questions[currentIndex]}
                givenAnswers={givenAnswers}
                setAnswer={setAnswer}
                goToNextQuestion={goToNextQuestion}
                respectAspectRatio={respectAspectRatio}
                isLastQuestion={currentIndex === quiz.questions.length - 1}
                isFirstQuestion={currentIndex === 0}
                seed={seed}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </LazyMotion>
  );
};

export default QuizGamePageContent;
